import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserPlus } from 'lucide-react';
import { Button, Card, Field, SelectField } from '@pixlide/ui';
import { ApiError } from '@/api/client';
import { useInviteTenantUser, useTenants } from '@/api/queries/platform-admin';
import { ROLE_LABELS, TENANT_ROLES } from '@/features/users/user-labels';
import { inviteUserSchema, type InviteUserInput } from '@/features/users/schemas';

/** Nombre de tenants proposés dans la liste déroulante (plafond de pagination de l'API). */
const TENANT_CHOICES_PAGE_SIZE = 100;

const EMPTY_INVITE: InviteUserInput = { tenantId: '', email: '', role: 1 };

/**
 * Invitation d'un utilisateur dans un compte client.
 *
 * Le back-office INVITE, il ne crée pas le compte de bout en bout : le mot de passe et
 * l'identité sont saisis par la personne à l'acceptation. Un administrateur plateforme qui
 * choisirait le mot de passe d'un client disposerait, jusqu'à ce qu'il soit changé, d'un accès
 * complet à un compte qui n'est pas le sien.
 */
export function InviteUserForm({ onDone }: { onDone: () => void }) {
  const invite = useInviteTenantUser();
  const [tenantSearch, setTenantSearch] = useState('');

  // La liste des tenants est plafonnée à 100 par l'API : le champ de recherche n'est pas un
  // confort, c'est le seul moyen d'atteindre un client au-delà de ce rang.
  const tenants = useTenants({
    search: tenantSearch.trim() === '' ? null : tenantSearch.trim(),
    pageSize: TENANT_CHOICES_PAGE_SIZE,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InviteUserInput>({
    resolver: zodResolver(inviteUserSchema),
    defaultValues: EMPTY_INVITE,
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await invite.mutateAsync(values);
      reset(EMPTY_INVITE);
      onDone();
    } catch {
      // Message rendu depuis `invite.error` ci-dessous.
    }
  });

  const apiError = invite.error instanceof ApiError ? invite.error : null;

  return (
    <Card className="flex flex-col gap-4 p-6">
      <h2 className="text-sm font-semibold text-ink-700">Inviter un utilisateur</h2>

      {apiError === null ? null : (
        <p role="alert" className="rounded-xl bg-danger-500/10 px-4 py-3 text-sm text-danger-500">
          {apiError.message}
        </p>
      )}

      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-3">
        <Field
          label="Rechercher un compte client"
          type="search"
          value={tenantSearch}
          onChange={(event) => setTenantSearch(event.target.value)}
          hint={
            tenants.data && tenants.data.totalCount > TENANT_CHOICES_PAGE_SIZE
              ? `${tenants.data.totalCount} comptes clients : affinez la recherche pour trouver le vôtre.`
              : undefined
          }
        />

        <SelectField
          label="Compte client"
          required
          error={errors.tenantId?.message}
          {...register('tenantId')}
        >
          <option value="">Sélectionnez un compte client…</option>
          {(tenants.data?.items ?? []).map((tenant) => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.companyName}
            </option>
          ))}
        </SelectField>

        <Field
          label="Adresse email"
          type="email"
          autoComplete="email"
          required
          error={errors.email?.message}
          {...register('email')}
        />

        <SelectField
          label="Rôle"
          required
          error={errors.role?.message}
          {...register('role', { valueAsNumber: true })}
        >
          {TENANT_ROLES.map((value) => (
            <option key={value} value={value}>
              {ROLE_LABELS[value]}
            </option>
          ))}
        </SelectField>

        <div className="mt-2 flex gap-2">
          <Button type="submit" isLoading={isSubmitting || invite.isPending} className="w-fit">
            <UserPlus aria-hidden className="size-4" />
            Envoyer l'invitation
          </Button>

          <Button type="button" variant="ghost" onClick={onDone}>
            Annuler
          </Button>
        </div>
      </form>
    </Card>
  );
}
